import React, { useState, useEffect } from 'react';

interface Tenant {
  id: number;
  tenant_code: string;
  tenant_name: string;
  contact_email: string;
  contact_phone?: string;
  address?: string;
  is_active: boolean;
  created_at: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  tenant_code: string;
  tenant_name: string;
  is_active: boolean;
  created_at: string;
}

interface AdminPanelProps {
  apiFetch: (url: string, options?: any) => Promise<any>;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ apiFetch }) => {
  const [activeTab, setActiveTab] = useState<'tenants' | 'users'>('tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 新規テナント作成用フォーム
  const [newTenant, setNewTenant] = useState({
    tenant_code: '',
    tenant_name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  // 新規ユーザー作成用フォーム
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    tenant_code: ''
  });

  const [showTenantForm, setShowTenantForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);

  useEffect(() => {
    if (activeTab === 'tenants') {
      loadTenants();
    } else {
      loadUsers();
    }
  }, [activeTab]);

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/tenants');
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setTenants(data);
    } catch (err: any) {
      setError('テナント一覧の取得に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/users');
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setUsers(data);
    } catch (err: any) {
      setError('ユーザー一覧の取得に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTenant)
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setNewTenant({
        tenant_code: '',
        tenant_name: '',
        contact_email: '',
        contact_phone: '',
        address: ''
      });
      setShowTenantForm(false);
      await loadTenants();
    } catch (err: any) {
      setError('テナント作成に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setNewUser({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'user',
        tenant_code: ''
      });
      setShowUserForm(false);
      await loadUsers();
    } catch (err: any) {
      setError('ユーザー作成に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleTenantStatus = async (tenantId: number) => {
    try {
      const res = await apiFetch(`/api/admin/tenants/${tenantId}/toggle`, { method: 'PUT' });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      await loadTenants();
    } catch (err: any) {
      setError('テナント状態の変更に失敗しました: ' + err.message);
    }
  };

  const toggleUserStatus = async (userId: number) => {
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/toggle`, { method: 'PUT' });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      await loadUsers();
    } catch (err: any) {
      setError('ユーザー状態の変更に失敗しました: ' + err.message);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>🛠️ テナント管理</h2>

      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          color: '#c33'
        }}>
          {error}
        </div>
      )}

      {/* タブメニュー */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('tenants')}
          style={{
            padding: '10px 20px',
            marginRight: '10px',
            backgroundColor: activeTab === 'tenants' ? '#007bff' : '#f8f9fa',
            color: activeTab === 'tenants' ? 'white' : '#333',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🏢 テナント管理
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'users' ? '#007bff' : '#f8f9fa',
            color: activeTab === 'users' ? 'white' : '#333',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          👥 ユーザー管理
        </button>
      </div>

      {/* テナント管理 */}
      {activeTab === 'tenants' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>テナント一覧</h3>
            <button
              onClick={() => setShowTenantForm(!showTenantForm)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ➕ 新規テナント追加
            </button>
          </div>

          {/* 新規テナント作成フォーム */}
          {showTenantForm && (
            <form onSubmit={createTenant} style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4>新規テナント作成</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>テナントコード *</label>
                  <input
                    type="text"
                    value={newTenant.tenant_code}
                    onChange={(e) => setNewTenant({ ...newTenant, tenant_code: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>テナント名 *</label>
                  <input
                    type="text"
                    value={newTenant.tenant_name}
                    onChange={(e) => setNewTenant({ ...newTenant, tenant_name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>連絡先メール *</label>
                  <input
                    type="email"
                    value={newTenant.contact_email}
                    onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>電話番号</label>
                  <input
                    type="text"
                    value={newTenant.contact_phone}
                    onChange={(e) => setNewTenant({ ...newTenant, contact_phone: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>住所</label>
                  <input
                    type="text"
                    value={newTenant.address}
                    onChange={(e) => setNewTenant({ ...newTenant, address: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '15px' }}>
                <button type="submit" disabled={loading} style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}>
                  作成
                </button>
                <button type="button" onClick={() => setShowTenantForm(false)} style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                  キャンセル
                </button>
              </div>
            </form>
          )}

          {/* テナント一覧テーブル */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>テナントコード</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>テナント名</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>連絡先</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>状態</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>作成日</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(tenant => (
                  <tr key={tenant.id}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{tenant.id}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{tenant.tenant_code}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{tenant.tenant_name}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {tenant.contact_email}
                      {tenant.contact_phone && <br />}
                      {tenant.contact_phone}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: tenant.is_active ? '#d4edda' : '#f8d7da',
                        color: tenant.is_active ? '#155724' : '#721c24'
                      }}>
                        {tenant.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {new Date(tenant.created_at).toLocaleDateString('ja-JP')}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <button
                        onClick={() => toggleTenantStatus(tenant.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: tenant.is_active ? '#dc3545' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {tenant.is_active ? '無効化' : '有効化'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ユーザー管理 */}
      {activeTab === 'users' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>ユーザー一覧</h3>
            <button
              onClick={() => setShowUserForm(!showUserForm)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ➕ 新規ユーザー追加
            </button>
          </div>

          {/* 新規ユーザー作成フォーム */}
          {showUserForm && (
            <form onSubmit={createUser} style={{
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4>新規ユーザー作成</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>ユーザー名 *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>メールアドレス *</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>氏名 *</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>パスワード *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>権限</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  >
                    <option value="user">一般ユーザー</option>
                    <option value="manager">管理者</option>
                    <option value="admin">システム管理者</option>
                  </select>
                </div>
                <div>
                  <label>テナントコード *</label>
                  <select
                    value={newUser.tenant_code}
                    onChange={(e) => setNewUser({ ...newUser, tenant_code: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  >
                    <option value="">選択してください</option>
                    {tenants.filter(t => t.is_active).map(tenant => (
                      <option key={tenant.tenant_code} value={tenant.tenant_code}>
                        {tenant.tenant_name} ({tenant.tenant_code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '15px' }}>
                <button type="submit" disabled={loading} style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}>
                  作成
                </button>
                <button type="button" onClick={() => setShowUserForm(false)} style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                  キャンセル
                </button>
              </div>
            </form>
          )}

          {/* ユーザー一覧テーブル */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>ユーザー名</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>氏名</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>メール</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>権限</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>テナント</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>状態</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{user.id}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{user.username}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{user.full_name}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{user.email}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: user.role === 'admin' ? '#ffeaa7' : user.role === 'manager' ? '#81ecec' : '#ddd',
                        fontSize: '12px'
                      }}>
                        {user.role === 'admin' ? 'システム管理者' : user.role === 'manager' ? '管理者' : '一般ユーザー'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{user.tenant_name}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: user.is_active ? '#d4edda' : '#f8d7da',
                        color: user.is_active ? '#155724' : '#721c24'
                      }}>
                        {user.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <button
                        onClick={() => toggleUserStatus(user.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: user.is_active ? '#dc3545' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {user.is_active ? '無効化' : '有効化'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          読み込み中...
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
